import type React from "react";
import { useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

interface ThumbnailUploaderProps {
  onUpload: (file: File) => void;
  previewUrl?: string;
}

const ThumbnailUploader: React.FC<ThumbnailUploaderProps> = ({
  onUpload,
  previewUrl,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | undefined>(previewUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB");
      return;
    }

    setLoading(true);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
      onUpload(file);
      setLoading(false);
    };
    reader.onerror = () => {
      setError("Failed to read file");
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreview(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Course Thumbnail
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {preview ? (
        <Paper
          sx={{
            p: 2,
            textAlign: "center",
            position: "relative",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Box
            component="img"
            src={preview}
            alt="Thumbnail preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "200px",
              borderRadius: 1,
              mb: 2,
            }}
          />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleRemove}
            fullWidth
          >
            Remove Image
          </Button>
        </Paper>
      ) : (
        <Paper
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          sx={{
            p: 3,
            textAlign: "center",
            border: "2px dashed #ccc",
            borderRadius: 2,
            cursor: "pointer",
            transition: "all 0.3s",
            backgroundColor: "#fafafa",
            "&:hover": {
              borderColor: "#1976d2",
              backgroundColor: "#f0f7ff",
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag and drop your thumbnail here
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                or
              </Typography>
              <Button variant="contained">Click to Browse</Button>
            </>
          )}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: "block", mt: 1 }}
      >
        Supported formats: JPG, PNG, GIF (Max 5MB)
      </Typography>
    </Box>
  );
};

export default ThumbnailUploader;
